// FileTableAgGridV2.tsx
import { Alert, Badge, Button, Loader } from "@mantine/core";
import React, { useCallback, useMemo, useRef } from "react";

import { IFileAgGrid, IValueLabel } from "@mctypes/global.types";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, IsRowSelectable } from "ag-grid-community";
import { formatShortDate } from "../../app/routes/admin/selectors";
import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";

type Props = {
  files: IFileAgGrid[];
  getSelectedFilesCb: (files: IFileAgGrid[]) => void;
  filterExtension?: IValueLabel;
  isLoading?: boolean;
};

const FileTableAgGrid: React.FC<Props> = ({ files, getSelectedFilesCb, filterExtension, isLoading }) => {
  const gridRef = useRef<AgGridReact<IFileAgGrid>>(null);

  const filteredRows = useMemo<IFileAgGrid[]>(() => {
    if (!files) return [];
    if (!filterExtension?.value) return files;
    const ext = filterExtension.value.toUpperCase();
    return files.filter((f) => f.name?.toUpperCase().endsWith(ext));
  }, [files, filterExtension]);

  const noFilesWithGivenFilter = !!filterExtension?.value && filteredRows.length === 0;

  const onSendSelected = useCallback(() => {
    const selected = gridRef.current?.api?.getSelectedRows?.() ?? [];
    getSelectedFilesCb(selected);
    gridRef.current?.api?.deselectAll?.();
  }, [getSelectedFilesCb]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Navn",
        filter: true,
        width: 400,
        resizable: true,
        // ✅ Låt kolumnen rita rutorna
        checkboxSelection: true,
        headerCheckboxSelection: true,
        showDisabledCheckboxes: true,
      },
      {
        field: "lastUpdated",
        headerName: "Sist endret",
        filter: true,
        type: ["dateColumn"],
        valueFormatter: (params) => formatShortDate(params.value),
      },
    ],
    [],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      suppressMovable: true,
    }),
    [],
  );

  const isRowSelectable = useMemo<IsRowSelectable>(() => {
    return (params) => params?.data?.disabled !== true;
  }, []);

  return (
    <div className="d-flex flex-column ag-theme-alpine h-100 w-100 overflow-auto">
      <div className="d-flex" style={{ height: 40 }}>
        {filterExtension?.value && (
          <Badge
            className="d-flex align-items-center mt-1 mb-1"
            color="gray"
            variant="light"
            leftSection={<i className="fa fa-filter fa-fw" />}
          >
            <b>{filterExtension.value}</b>
          </Badge>
        )}
        {isLoading === true && (
          <div className="d-flex align-items-center ml-2 text-muted">
            <Loader className="mr-2" size="xs" />
            <span>Henter filer...</span>
          </div>
        )}
        <div className="flex-fill" />
        <Button
          className="mt-1 mb-1"
          color="mc-blue"
          disabled={isLoading === true}
          onClick={onSendSelected}
          title="Send valgte filer"
        >
          <i className="fa fa-angle-double-right" />
        </Button>
      </div>

      <div className="flex-fill" />

      {isLoading === true ? (
        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
          <Loader className="mr-2" size="sm" />
          <span>Henter filer...</span>
        </div>
      ) : noFilesWithGivenFilter ? (
        <Alert color="blue" variant="light">
          Ingen {filterExtension?.label}-filer i denne mappen
        </Alert>
      ) : (
        <AgGridReact<IFileAgGrid>
          ref={gridRef}
          rowData={filteredRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={true}
          // ✅ Klassisk API (hindrar ekstra urvalskolumn i nyere AG Grid)
          rowSelection="multiple"
          suppressRowClickSelection={true}
          isRowSelectable={isRowSelectable}
          localeText={AG_GRID_LOCALE_NOR}
        />
      )}
    </div>
  );
};

export default FileTableAgGrid;
